import { useState } from "react";
import styled from "styled-components";
import { add, endOfMonth, format, getDay, isSameDay, startOfMonth, startOfToday } from "date-fns";
import { es } from "date-fns/locale";
import m from "../../../styles/molanturTheme";
import { IconButton } from "./index";

interface RangeCalendarProps {
    from: Date
    to?: Date
    // Rango (por defecto): un clic = ese día solo (to undefined); el segundo clic cierra el rango y
    // el calendario. Con `single`, un clic elige el día y cierra.
    onChange: (from: Date, to?: Date) => void
    onClose: () => void
    single?: boolean
    // Días anteriores a este no se pueden elegir.
    min?: Date
    align?: 'center' | 'right'
}

export default function RangeCalendar({ from, to, onChange, onClose, single, min, align = 'center' }: RangeCalendarProps) {
    const [month, setMonth] = useState<Date>(startOfMonth(from))
    const [pending, setPending] = useState<Date>()
    const today = startOfToday()
    const last = endOfMonth(month).getDate()
    // Semana de lunes a domingo: getDay() da 0 para domingo.
    const blanks = (getDay(month) + 6) % 7
    const end = to || from

    function pick(day: Date) {
        if (single) {
            onChange(day, undefined)
            onClose()
            return
        }
        if (!pending) {
            setPending(day)
            onChange(day, undefined)
            return
        }
        const [a, b] = pending <= day ? [pending, day] : [day, pending]
        setPending(undefined)
        onChange(a, isSameDay(a, b) ? undefined : b)
        onClose()
    }

    return (
        <Main role="dialog" aria-label="Elegir fechas" $align={align}>
            <div className="month">
                <IconButton name="chevronLeft" label="Mes anterior" size={36} onClick={() => setMonth(add(month, { months: -1 }))} />
                <span>{format(month, 'LLLL yyyy', { locale: es })}</span>
                <IconButton name="chevronRight" label="Mes siguiente" size={36} onClick={() => setMonth(add(month, { months: 1 }))} />
            </div>
            <div className="grid">
                {['L', 'M', 'M', 'J', 'V', 'S', 'D'].map((w, i) => <span key={i} className="dow">{w}</span>)}
                {Array.from({ length: blanks }, (_, i) => <span key={`b${i}`} />)}
                {Array.from({ length: last }, (_, i) => {
                    const day = add(month, { days: i })
                    const edge = isSameDay(day, from) || isSameDay(day, end)
                    const inRange = day >= from && day <= end
                    return (
                        <button
                            key={i}
                            type="button"
                            className={`${edge ? 'edge' : inRange ? 'in' : ''} ${isSameDay(day, today) ? 'today' : ''}`}
                            aria-label={format(day, "d 'de' MMMM", { locale: es })}
                            aria-pressed={edge || inRange}
                            disabled={!!min && day < min}
                            onClick={() => pick(day)}
                        >{i + 1}</button>
                    )
                })}
            </div>
            {!single && <p>{pending ? 'Elige el día final del rango' : 'Un clic para un día · dos para un rango'}</p>}
        </Main>
    )
}

const Main = styled.div<{ $align: 'center' | 'right' }>`
    position: absolute;
    top: calc(100% + 8px);
    /* Alineado a la derecha vive dentro de la tira de días: nunca más ancho que ella, o se sale por la izquierda. */
    ${p => p.$align === 'right' ? 'right: 0; max-width: 100%;' : 'left: 50%; transform: translateX(-50%); max-width: calc(100vw - 24px);'}
    z-index: 40;
    width: 320px;
    padding: 18px;
    background: ${m.color.surface};
    border-radius: ${m.radius.lg};
    box-shadow: 0 24px 60px -18px rgba(43,38,32,0.45);
    color: ${m.color.ink};

    .month {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 10px;
        span {
            font-family: ${m.font.display};
            font-weight: 600;
            font-size: 18px;
            text-transform: capitalize;
        }
    }
    .grid {
        display: grid;
        grid-template-columns: repeat(7, 1fr);
        gap: 2px;
    }
    .dow {
        padding: 4px 0 6px;
        text-align: center;
        font-family: ${m.font.label};
        font-size: 11px;
        font-weight: 600;
        color: ${m.color.inkFaint};
    }
    .grid button {
        height: 38px;
        border: none;
        border-radius: 10px;
        background: transparent;
        color: ${m.color.ink};
        font-family: ${m.font.text};
        font-size: 13.5px;
        font-weight: 500;
        cursor: pointer;
        &:disabled {
            color: ${m.color.inkFaint};
            opacity: 0.45;
            cursor: not-allowed;
        }
    }
    .grid button.today { box-shadow: inset 0 0 0 1.5px ${m.color.primary}; }
    .grid button.in {
        background: ${m.color.primarySoft};
        border-radius: 4px;
    }
    .grid button.edge {
        background: ${m.color.ink};
        color: #fff;
        font-weight: 700;
        box-shadow: none;
    }
    p {
        margin: 10px 0 0;
        text-align: center;
        font-size: 12px;
        color: ${m.color.inkFaint};
    }
`
